/**
 * Role Use Cases Factory
 *
 * Migrated from the legacy App/composition/personnelComposition bridge so role
 * dependencies are assembled inside the personnel domain composition root.
 *
 * @module @personnel/composition/factories/createRoleUseCases
 */

import type { AxiosInstance } from 'axios';
import type { RoleUseCases } from '@personnel/composition/types';

import { createRoleHttpClient } from '@personnel/infrastructure/api/RoleHttpClient';
import { getRolesUseCase } from '@personnel/application/use-cases/GetRolesUseCase';
import { createRoleUseCase } from '@personnel/application/use-cases/CreateRoleUseCase';
import { updateRoleUseCase } from '@personnel/application/use-cases/UpdateRoleUseCase';
import { deleteRoleUseCase } from '@personnel/application/use-cases/DeleteRoleUseCase';
import { getRoleByUuidUseCase } from '@personnel/application/use-cases/GetRoleByUuidUseCase';

/**
 * Assembles all role use cases with their injected dependencies.
 * The Role HTTP client implements IRoleApiPort directly (no separate repository).
 *
 * @param httpClient - Axios instance for the connect API
 * @returns Fully composed role use cases
 */
export const createRoleUseCases = (httpClient: AxiosInstance): RoleUseCases => {
  const apiPort = createRoleHttpClient({ httpClient });

  return {
    getRoles: getRolesUseCase({ apiPort }),
    createRole: createRoleUseCase({ apiPort }),
    updateRole: updateRoleUseCase({ apiPort }),
    deleteRole: deleteRoleUseCase({ apiPort }),
    getRoleByUuid: getRoleByUuidUseCase({ apiPort }),
  };
};
